import { useEffect, useRef, useState } from "react";
import CircularProgress from "../ui/CircularProgress";
import "./TreeListItem.css";

export interface TreeListItemData {
    id: string;
    title: string;
    /** Chapters completed, out of `total` chapters — drives the ring below. */
    solved: number;
    total: number;
    createdAt: string | number | Date;
}

export type TreeStatus = "VALIDATING" | "QUESTIONS" | "CHAPTERS" | "NODES" | "COMPLETE" | "ERROR";

interface TreeListItemProps {
    tree: TreeListItemData;
    status: TreeStatus;
    /** Whether the QUESTIONS stage's questions actually exist yet — the
     *  backend flips `status` to QUESTIONS before generating and saving
     *  them, so status alone isn't enough to know there's anything to
     *  answer yet. Ignored for every other status. */
    questionsReady?: boolean;
    active?: boolean;
    onSelect?: (id: string) => void;
    onRename?: (id: string) => void;
    onDelete?: (id: string) => void;
}

export default function TreeListItem({
                                         tree,
                                         status,
                                         questionsReady = false,
                                         active = false,
                                         onSelect,
                                         onRename,
                                         onDelete,
                                     }: TreeListItemProps) {
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!menuOpen) return;
        const handlePointerDown = (event: PointerEvent) => {
            if (menuRef.current && event.target instanceof Node && menuRef.current.contains(event.target)) {
                return;
            }
            setMenuOpen(false);
        };
        document.addEventListener("pointerdown", handlePointerDown, true);
        return () => document.removeEventListener("pointerdown", handlePointerDown, true);
    }, [menuOpen]);

    const percent = tree.total > 0 ? Math.round((tree.solved / tree.total) * 100) : 0;
    // QUESTIONS before the questions themselves exist reads the same as
    // still-generating — there's nothing to answer yet, so show the spinner
    // rather than the "?" icon a beat early.
    const showQuestionIcon = status === "QUESTIONS" && questionsReady;
    // CHAPTERS/NODES are the generation stages that resume once the user has
    // answered the QUESTIONS stage's questions — same spinner as VALIDATING.
    const isLoading = status === "VALIDATING" || status === "CHAPTERS" || status === "NODES"
        || (status === "QUESTIONS" && !questionsReady);

    return (
        <div className={`tree-item ${active ? "tree-item--active" : ""}`}>
            <button className="tree-item__main" onClick={() => onSelect?.(tree.id)}>
                {status === "COMPLETE" && (
                    <CircularProgress value={percent} size={38} strokeWidth={3}/>
                )}

                {showQuestionIcon && (
                    <span className="tree-item__question-icon" role="status" aria-label="Waiting on your answers">
            <svg viewBox="0 0 38 38" width={38} height={38} aria-hidden="true">
              <circle cx="19" cy="19" r="16" fill="none" strokeWidth="3"/>
              <text x="19" y="20" textAnchor="middle" dominantBaseline="central">?</text>
            </svg>
          </span>
                )}

                {isLoading && (
                    <span
                        className="tree-item__spinner"
                        role="status"
                        aria-label={status === "VALIDATING" ? "Validating" : "Generating"}
                    >
            <svg viewBox="0 0 38 38" width={38} height={38} aria-hidden="true">
              <circle
                  className="tree-item__spinner-track"
                  cx="19" cy="19" r="16"
                  fill="none"
              />
              <circle
                  className="tree-item__spinner-arc"
                  cx="19" cy="19" r="16"
                  fill="none"
                  strokeLinecap="round"
              />
            </svg>
          </span>
                )}

                {status === "ERROR" && (
                    <span className="tree-item__error-icon" role="status" aria-label="Error">
            <svg viewBox="0 0 38 38" width={38} height={38} aria-hidden="true">
              <circle cx="19" cy="19" r="16" fill="none" strokeWidth="3"/>
              <line x1="19" y1="12" x2="19" y2="21" strokeWidth="2.5" strokeLinecap="round"/>
              <circle cx="19" cy="26" r="1.7" fill="currentColor"/>
            </svg>
          </span>
                )}

                <span className="tree-item__title">{tree.title}</span>
            </button>

            <div className="tree-item__menu" ref={menuRef}>
                <button
                    className="tree-item__dots"
                    type="button"
                    aria-label={`Options for ${tree.title}`}
                    aria-expanded={menuOpen}
                    onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpen((v) => !v);
                    }}
                >
                    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                        <circle cx="10" cy="4" r="1.6" fill="currentColor"/>
                        <circle cx="10" cy="10" r="1.6" fill="currentColor"/>
                        <circle cx="10" cy="16" r="1.6" fill="currentColor"/>
                    </svg>
                </button>

                {menuOpen && (
                    <div className="tree-item__dropdown" role="menu">
                        <button
                            role="menuitem"
                            type="button"
                            onClick={() => {
                                onRename?.(tree.id);
                                setMenuOpen(false);
                            }}
                        >
                            Rename
                        </button>
                        <button
                            role="menuitem"
                            type="button"
                            className="tree-item__dropdown-delete"
                            onClick={() => {
                                onDelete?.(tree.id);
                                setMenuOpen(false);
                            }}
                        >
                            Delete
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}